import FVector from '@data/geometry/FVector';
import Grid from 'Geodata/grid';
import * as THREE from 'three';
import { CSM } from 'three/examples/jsm/csm/CSM.js';

export class ShadowBuilder {
    scene: THREE.Scene;
    renderer: THREE.WebGLRenderer;
    isEnabled: boolean = false;
    sunLight: THREE.DirectionalLight;
    ambientLight: THREE.AmbientLight;
    private camera?: THREE.Camera;
    private csm?: CSM;

    constructor(renderer: THREE.WebGLRenderer, scene: THREE.Scene) {
        this.renderer = renderer;
        this.scene = scene;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        const { sunLight, ambientLight } = this.initLight();
        this.sunLight = sunLight;
        this.ambientLight = ambientLight;
    }

    dispose() {
        if (!this.isEnabled) return;

        this.renderer.shadowMap.enabled = false;
        this.renderer.shadowMap.autoUpdate = false;

        this.disposeCsm();
        this.scene.remove(this.sunLight);
        this.scene.remove(this.sunLight.target);
        this.scene.remove(this.ambientLight);
        this.isEnabled = false;
    }

    enable() {
        if (!this.isEnabled) {
            this.renderer.shadowMap.enabled = true; // Abilita l'ombreggiatura
            this.renderer.shadowMap.autoUpdate = true; // Abilita l'ombreggiatura

            this.ensureCsm();
            if (!this.csm) {
                this.updateShadowFrustumFromScene();
                this.scene.add(this.sunLight);
                this.scene.add(this.sunLight.target);
            }
            this.scene.add(this.ambientLight);
            this.isEnabled = true;
        }
    }

    setCamera(camera: THREE.Camera) {
        const hasCameraChanged = this.camera !== camera;
        this.camera = camera;
        if (this.csm && hasCameraChanged) {
            this.csm.camera = camera;
            this.csm.updateFrustums();
        }
    }

    initLight(): { sunLight: THREE.DirectionalLight; ambientLight: THREE.AmbientLight } {
        const sunLight = new THREE.DirectionalLight(0xffffff, 5);

        // Shadow details options:
        const maxMapSize = this.renderer.capabilities.maxTextureSize;
        const shadow_mapSize = Math.min(8192, maxMapSize);
        sunLight.shadow.mapSize.width = shadow_mapSize;
        sunLight.shadow.mapSize.height = shadow_mapSize;

        // Initialize with safe defaults; real bounds are fitted from scene in updateShadowFrustumFromScene().
        sunLight.shadow.camera.near = 1;
        sunLight.shadow.camera.far = 12000;
        sunLight.shadow.camera.left = -1500;
        sunLight.shadow.camera.right = 1500;
        sunLight.shadow.camera.top = 1500;
        sunLight.shadow.camera.bottom = -1500;

        // SunLight position:
        const sunDir = this.getYearlyDirection().mult(-1).normalize();
        sunLight.position.set(sunDir.x * 5000, sunDir.y * 5000, sunDir.z * 5000);
        sunLight.castShadow = true;

        // Add basic ambient light:
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.7);

        return { sunLight, ambientLight };
    }

    updatePosition(sunDir: FVector) {
        sunDir.normalize();
        // Light travels from sun toward scene, so CSM light direction is the negated sun vector.
        if (this.csm) {
            this.csm.lightDirection.set(-sunDir.x, -sunDir.y, -sunDir.z).normalize();
            this.csm.updateFrustums();
        }
        this.sunLight.position.set(sunDir.x * 5000, sunDir.y * 5000, sunDir.z * 5000);
        this.updateShadowFrustumFromScene();
    }

    updateForFrame(camera: THREE.Camera) {
        this.setCamera(camera);
        if (!this.isEnabled) return;
        if (this.csm) {
            this.csm.update();
        }
    }

    private updateShadowFrustumFromScene() {
        // 1) Collect bounds from meshes that cast shadows (casters):
        const bounds = this.computeShadowRelevantBounds();
        if (!bounds) {
            return;
        }

        // 2) Derive center/size used to steer light target and fit orthographic shadow camera:
        const center = new THREE.Vector3();
        const size = new THREE.Vector3();
        bounds.getCenter(center);
        bounds.getSize(size);

        // 2.1) Keep the directional light target aligned with the active city center:
        this.sunLight.target.position.copy(center);
        this.sunLight.target.updateMatrixWorld(true);

        // 2.2) Fit the shadow camera around the caster footprint with safety clamps:
        const orthoHalf = Math.min(2600, Math.max(600, Math.max(size.x, size.y) * 0.6 + 200));
        const verticalSpan = Math.max(size.z, 1);
        const cam = this.sunLight.shadow.camera as THREE.OrthographicCamera;
        cam.left = -orthoHalf;
        cam.right = orthoHalf;
        cam.top = orthoHalf;
        cam.bottom = -orthoHalf;
        cam.near = 1;
        cam.far = Math.max(2500, verticalSpan + 4000);
        cam.updateProjectionMatrix();

        // 3) Request shadow-map refresh after frustum/target changes:
        this.sunLight.shadow.needsUpdate = true;
    }

    private computeShadowRelevantBounds(): THREE.Box3 | null {
        // 1) Scan visible meshes that cast shadows:
        const bounds = new THREE.Box3();
        let hasGeometry = false;

        this.scene.traverse((obj) => {
            const mesh = obj as THREE.Mesh;
            if (!mesh.isMesh) return;
            if (!mesh.visible) return;
            // 1.1) Use casters only to avoid huge receiver meshes (terrain) exploding frustum size.
            if (!mesh.castShadow) return;

            bounds.expandByObject(mesh);
            hasGeometry = true;
        });

        // 2) Return null if no valid caster bounds were found:
        if (!hasGeometry || bounds.isEmpty()) {
            return null;
        }
        return bounds;
    }

    // =====================================
    // CASCADE SHADOW MAPPING
    // =====================================

    private ensureCsm() {
        if (this.csm || !this.camera) return;

        const maxMapSize = this.renderer.capabilities.maxTextureSize;
        const shadowMapSize = Math.min(8192, maxMapSize);

        this.csm = new CSM({
            camera: this.camera,
            parent: this.scene,
            cascades: 3,
            mode: 'practical',
            maxFar: 5200,
            shadowMapSize,
            // shadowBias: -0.001,
            lightDirection: new THREE.Vector3(this.sunLight.position.x, this.sunLight.position.y, this.sunLight.position.z)
                .normalize()
                .negate(),
            // Keep total lighting energy stable after changing cascade count.
            lightIntensity: this.sunLight.intensity / 6,
            lightNear: 1,
            lightFar: 5200,
            lightMargin: 350,
        });
        // Blend split boundaries to hide cascade "levels" while rotating.
        this.csm.fade = true;
        this.tuneCsmLights();
        this.setupCsmMaterials();
    }

    private tuneCsmLights() {
        if (!this.csm) return;
        for (let i = 0; i < this.csm.lights.length; i++) {
            const light = this.csm.lights[i];
            light.shadow.bias = -0.0002;
            // // Near cascade is the most prone to rooftop acne; push normal bias there only.
            // light.shadow.normalBias = 0.001;
            light.shadow.radius = 1;
            light.shadow.blurSamples = 8;
        }
    }

    private setupCsmMaterials() {
        if (!this.csm) return;

        this.scene.traverse((obj) => {
            const mesh = obj as THREE.Mesh;
            if (!mesh.isMesh || !mesh.material) return;
            const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
            for (const material of materials) {
                try {
                    this.csm?.setupMaterial(material);
                    material.needsUpdate = true;
                } catch {
                    // Some custom/unlit materials are not compatible with CSM setup.
                }
            }
        });
    }

    private disposeCsm() {
        if (!this.csm) return;
        this.csm.remove();
        this.csm.dispose();
        this.csm = undefined;
    }

    /**
     * Computes the average (yearly mean) unit vector pointing **from the site toward the Sun**
     * at a fixed UTC time (default 12:00). This represents the general or "typical"
     * sun direction for the site's latitude and longitude, averaged across all days
     * of a year when the sun is above the horizon.
     *
     * The vector is expressed in local ENU coordinates:
     *  - X → East
     *  - Y → Up
     *  - Z → North
     *
     * If you need the light (shadow) direction instead (sunlight traveling down),
     * use the negated vector.
     *
     * @returns {FVector} Unit vector pointing from the site toward the Sun.
     */
    getYearlyDirection() {
        const latDeg = Grid.siteLat;
        const lonDeg = Grid.siteLon;
        const toRad = (d: number) => (d * Math.PI) / 180;
        const lat = toRad(latDeg);

        function solarVectorAtDay(N: number, hourUTC = 12) {
            const γ = ((2 * Math.PI) / 365) * (N - 1 + (hourUTC - 12) / 24);
            const δ =
                0.006918 -
                0.399912 * Math.cos(γ) +
                0.070257 * Math.sin(γ) -
                0.006758 * Math.cos(2 * γ) +
                0.000907 * Math.sin(2 * γ) -
                0.002697 * Math.cos(3 * γ) +
                0.00148 * Math.sin(3 * γ);
            const E =
                229.18 *
                (0.000075 + 0.001868 * Math.cos(γ) - 0.032077 * Math.sin(γ) - 0.014615 * Math.cos(2 * γ) - 0.040849 * Math.sin(2 * γ));
            const Tst = (hourUTC * 60 + E + 4 * lonDeg) % 1440;
            const H = ((Tst / 4 - 180) * Math.PI) / 180;

            const sinEl = Math.sin(lat) * Math.sin(δ) + Math.cos(lat) * Math.cos(δ) * Math.cos(H);
            const el = Math.asin(Math.max(-1, Math.min(1, sinEl)));

            if (el <= 0) return null; // sun below horizon

            const az = Math.atan2(Math.sin(H), Math.cos(H) * Math.sin(lat) - Math.tan(δ) * Math.cos(lat)) + Math.PI;

            const x = Math.cos(el) * Math.sin(az); // East
            const y = Math.sin(el); // Up
            const z = Math.cos(el) * Math.cos(az); // North
            return { x, y, z };
        }

        let sx = 0,
            sy = 0,
            sz = 0;
        for (let N = 1; N <= 365; N++) {
            const v = solarVectorAtDay(N);
            if (!v) continue;
            sx += v.x;
            sy += v.y;
            sz += v.z;
        }

        const len = Math.hypot(sx, sy, sz) || 1;
        return new FVector(sx / len, sy / len, sz / len);
    }
}
